import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {Heart, Menu, X, User, LogOut} from 'lucide-react';
import { Button } from './ui/Button';
import { useAuth } from '../hooks/useAuth';
import { NotificationSystem } from './NotificationSystem';

export function Navbar() {
  const { user, signIn, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-lg shadow-md'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group" aria-label="Página inicial do Doe">
          <div className="relative">
            <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-[#E53E3E] fill-current transition-transform group-hover:scale-110" aria-hidden="true" />
            <div className="absolute inset-0 bg-[#E53E3E] opacity-20 rounded-full blur-md group-hover:opacity-30 transition-opacity"></div>
          </div>
          <div>
            <span className="text-xl sm:text-2xl font-bold text-gray-900">Doe</span>
            <span className="block text-[10px] sm:text-xs text-gray-500 -mt-1">ConectaVidas</span>
          </div>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Início
          </Link>
          <Link to="/solicitacoes" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Solicitações
          </Link>
          <Link to="/sou-doador" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Sou Doador
          </Link>
          <Link to="/dashboard" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Impacto
          </Link>
          <Link to="/ranking" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Ranking
          </Link>
          <Link to="/mapa" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Mapa
          </Link>
          <Link to="/sobre" className="text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors">
            Sobre
          </Link>
        </div>

        <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
          <NotificationSystem />
          {user ? (
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 max-w-[120px] xl:max-w-none truncate">Olá, {user.userName}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => signOut()}
                className="border-gray-300 hover:border-[#E53E3E] hover:text-[#E53E3E]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          ) : (
            <Button 
              onClick={() => signIn()} 
              className="bg-[#E53E3E] hover:bg-red-600 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <User className="h-4 w-4 mr-2" />
              Entrar
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? (
            <X className="h-6 w-6 text-gray-900" />
          ) : (
            <Menu className="h-6 w-6 text-gray-900" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="container mx-auto px-4 py-6 space-y-4">
            <Link
              to="/"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Início
            </Link>
            <Link
              to="/solicitacoes"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Solicitações
            </Link>
            <Link
              to="/sou-doador"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Sou Doador
            </Link>
            <Link
              to="/ranking"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Ranking
            </Link>
            <Link
              to="/mapa"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Mapa
            </Link>
            <Link
              to="/sobre"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Sobre
            </Link>
            <Link
              to="/dashboard"
              className="block text-sm font-medium text-gray-700 hover:text-[#E53E3E] transition-colors py-2"
              onClick={() => setIsMenuOpen(false)}
            >
              Impacto
            </Link>
            <div className="pt-4 border-t border-gray-100">
              {user ? (
                <div className="space-y-3">
                  <span className="block text-sm text-gray-600">Olá, {user.userName}</span>
                  <Button
                    variant="outline"
                    className="w-full justify-start border-gray-300 hover:border-[#E53E3E] hover:text-[#E53E3E]"
                    onClick={() => signOut()}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </Button>
                </div>
              ) : (
                <Button
                  className="w-full bg-[#E53E3E] hover:bg-red-600 text-white shadow-lg"
                  onClick={() => signIn()}
                >
                  <User className="h-4 w-4 mr-2" />
                  Entrar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
